import {
  combineLatestTyped,
  filterNotUndefined,
  mergeTyped,
  roundToInt,
  useDataStream,
  useStateAsStream,
  useStreamValue,
  useVoidEventAsStream,
  valueIsNot
} from 'annotation-tools/utils';
import { useCallback, useMemo } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { Observable, timer } from 'rxjs';
import { filter, map, mapTo, startWith, switchMap } from 'rxjs/operators';
import tus from 'tus-js-client';
import {
  StreamValueType,
  uploadFileByTus
} from '../api/fetch-functions/upload-image';
import { viewTexts } from '../constants/view-texts';
import { token$ } from '../global-state/auth-state';
import { tusErrorMessageConverter } from './error-message-converter';
import { getFileGuidFromUrl } from './get-guid-from-url';

const vt = viewTexts.tusUploader;

export const useTusUploaderSingleFile = (
  autoHideErrorMessageDuration: number
) => {
  const [cancelClicked$, cancelUploading] = useVoidEventAsStream();

  const [dropAreaEvent$, setDropAreaEvent] = useStateAsStream<
    | { type: 'standby' }
    | { type: 'accepted-files'; files: File[] }
    | { type: 'no-multiple-uploads-error' }
  >({ type: 'standby' });

  /* streams */

  const droppedFiles$ = useDataStream<undefined | File[]>(
    undefined,
    dropAreaEvent$.pipe(
      map(x => (x.type === 'accepted-files' ? x.files : undefined))
    )
  );

  const dropppedFileFiltered$ = useDataStream<undefined | File>(
    undefined,
    droppedFiles$.pipe(
      map(files =>
        files === undefined || files.length !== 1 ? undefined : files[0]
      )
    )
  );

  const tusUploadTask$ = useDataStream<
    [Observable<StreamValueType>, tus.Upload] | undefined
  >(
    undefined,
    combineLatestTyped(
      dropppedFileFiltered$.pipe(filterNotUndefined()),
      token$
    ).pipe(
      map(([file, tkn]) =>
        tkn === undefined ? undefined : uploadFileByTus(tkn, file)
      )
    )
  );

  const tusUploadState$ = useDataStream<
    { type: 'standby' } | { type: 'cancel' } | StreamValueType
  >(
    { type: 'standby' },
    mergeTyped(
      cancelClicked$.pipe(mapTo({ type: 'cancel' as const })),
      tusUploadTask$.pipe(
        filterNotUndefined(),
        switchMap(([state, _uploader]) => state)
      )
    )
  );

  const tusUploader$ = useDataStream<undefined | tus.Upload>(
    undefined,
    tusUploadTask$.pipe(map(v => (v === undefined ? undefined : v[1])))
  );

  const uploadPercentage$ = useDataStream<undefined | number>(
    undefined,
    tusUploadState$.pipe(
      map(e => {
        if (e === undefined || e.type !== 'progress') return undefined;
        const [sent, total] = e.value;
        return roundToInt((100 * sent) / total);
      })
    )
  );

  const isUploading$ = useDataStream<boolean>(
    false,
    uploadPercentage$.pipe(valueIsNot(undefined))
  );

  const errorMessage$ = useDataStream<string>(
    '',
    mergeTyped(
      token$.pipe(
        map(tkn => (tkn === undefined ? viewTexts.tokenExpiredMessage : ''))
      ),
      dropAreaEvent$.pipe(
        filter(e => e.type === 'no-multiple-uploads-error'),
        mapTo(vt.noParallelUpload)
      ),
      droppedFiles$.pipe(
        filterNotUndefined(),
        map(e => (e.length !== 1 ? vt.numUploadFileRestriction : ''))
      ),
      cancelClicked$.pipe(mapTo('キャンセルされました')),
      tusUploadState$.pipe(
        filterNotUndefined(),
        map(e => {
          switch (e.type) {
            case 'completed':
              return '';
            case 'error':
              return tusErrorMessageConverter(e.value.message).message;
            case 'progress':
            case 'standby':
            case 'cancel':
            default:
              return undefined;
          }
        }),
        filterNotUndefined()
      )
    )
  );

  const autoHideErrorMessage$ = useDataStream<string>(
    '',
    errorMessage$.pipe(
      switchMap(msg =>
        timer(autoHideErrorMessageDuration).pipe(mapTo(''), startWith(msg))
      )
    )
  );

  const uploadCompletedFileGuid$ = useDataStream<string | undefined>(
    undefined,
    tusUploadState$.pipe(
      filterNotUndefined(),
      filter(e => e.type === 'completed' && e.url !== undefined),
      map(
        e =>
          e.type === 'completed' && e.url !== undefined
            ? getFileGuidFromUrl(e.url)
            : undefined // dummy
      )
    )
  );

  /* extract values */

  const errorMessage = useStreamValue(errorMessage$, '');
  const autoHideErrorMessage = useStreamValue(autoHideErrorMessage$, '');
  const isUploading = useStreamValue(isUploading$, false);
  const uploadPercentage = useStreamValue(
    uploadPercentage$.pipe(filterNotUndefined()),
    0
  );

  const tusUploader = useStreamValue(tusUploader$);

  /* callbacks */

  const onDrop = useCallback(
    <T extends File>(acceptedFiles: T[]) => {
      if (isUploading) {
        setDropAreaEvent({ type: 'no-multiple-uploads-error' });
      } else {
        setDropAreaEvent({ type: 'accepted-files', files: acceptedFiles });
      }
    },
    [setDropAreaEvent, isUploading]
  );

  const cancelOnClick = useCallback(() => {
    cancelUploading();
    if (tusUploader !== undefined) {
      tusUploader.abort();
    }
  }, [tusUploader, cancelUploading]);

  /* dropzone configs */

  const dropzoneConfigs = useMemo<DropzoneOptions>(() => ({ onDrop }), [
    onDrop
  ]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone(
    dropzoneConfigs
  );

  const dropzoneRootProps = useMemo(getRootProps, [getRootProps]);
  const dropzoneInputProps = useMemo(getInputProps, [getInputProps]);

  return {
    dropzoneRootProps,
    dropzoneInputProps,
    isDragActive,
    errorMessage,
    autoHideErrorMessage,
    isUploading,
    uploadPercentage,
    cancelOnClick,
    uploadCompletedFileGuid$
  };
};
